import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PrismBase } from '@optiflow/types';
import { cn } from '@/utils/cn';
import {
  normalisePower,
  type EyeField,
  type EyeForm,
  type EyeSide,
  type FormErrors,
} from '../examinationForm';

const COLUMNS: { field: EyeField; abbreviation: string }[] = [
  { field: 'sphere', abbreviation: 'SPH' },
  { field: 'cylinder', abbreviation: 'CYL' },
  { field: 'axis', abbreviation: 'AXIS' },
  { field: 'add', abbreviation: 'ADD' },
  { field: 'prism', abbreviation: '' },
  { field: 'prismBase', abbreviation: '' },
];

const EYES: { eye: EyeSide; abbreviation: 'OD' | 'OS' }[] = [
  { eye: 'right', abbreviation: 'OD' },
  { eye: 'left', abbreviation: 'OS' },
];

const POWER_KIND: Partial<Record<EyeField, 'signed' | 'addition' | 'prism'>> = {
  sphere: 'signed',
  cylinder: 'signed',
  add: 'addition',
  prism: 'prism',
};

export interface EyePair {
  right: EyeForm;
  left: EyeForm;
}

interface ClinicalEyeGridProps {
  caption: string;
  value: EyePair;
  /** Absent: the grid is read-only. */
  onChange?: (next: EyePair) => void;
  errors?: FormErrors;
  errorPath?: (eye: EyeSide, field: EyeField) => string;
}

/**
 * One refraction or prescription as optometrists write it: OD above OS, SPH CYL AXIS ADD and prism across.
 *
 * The grid reads left to right in every language, so an Arabic page can never reorder a prescription. Values stay the text that was typed; leaving a power field only tidies it to two decimals and an explicit sign, from integer hundredths.
 */
export function ClinicalEyeGrid({
  caption,
  value,
  onChange,
  errors = {},
  errorPath,
}: ClinicalEyeGridProps) {
  const { t } = useTranslation();
  const baseId = useId();
  const readOnly = !onChange;

  const label = (field: EyeField) =>
    field === 'prism'
      ? t('medical.refraction.prismColumn')
      : field === 'prismBase'
        ? t('medical.refraction.baseColumn')
        : (COLUMNS.find((column) => column.field === field)?.abbreviation ?? field);

  const messages = EYES.flatMap(({ eye, abbreviation }) =>
    COLUMNS.flatMap(({ field }) => {
      const error = errorPath ? errors[errorPath(eye, field)] : undefined;
      return error
        ? [{ id: `${baseId}-${eye}-${field}-error`, eye, field, abbreviation, error }]
        : [];
    }),
  );
  const messageId = (eye: EyeSide, field: EyeField) =>
    messages.find((message) => message.eye === eye && message.field === field)?.id;

  const update = (eye: EyeSide, field: EyeField, next: string) => {
    if (!onChange) return;
    onChange({ ...value, [eye]: { ...value[eye], [field]: next } });
  };

  return (
    <div>
      <div dir="ltr" className="overflow-x-auto">
        <table className="w-full min-w-[40rem] border-separate border-spacing-x-2 border-spacing-y-1.5 text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              <td className="w-12" />
              {COLUMNS.map(({ field }) => (
                <th key={field} scope="col" className="text-muted text-start text-xs font-semibold">
                  <abbr title={t(`medical.refraction.fields.${field}`)} className="no-underline">
                    {label(field)}
                  </abbr>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EYES.map(({ eye, abbreviation }) => (
              <tr key={eye}>
                <th scope="row" className="text-ink text-start font-semibold">
                  <abbr title={t(`medical.eyes.${abbreviation}`)} className="no-underline">
                    {abbreviation}
                  </abbr>
                </th>
                {COLUMNS.map(({ field }) => {
                  const cellLabel = t('medical.refraction.cell', {
                    eye: abbreviation,
                    field: t(`medical.refraction.fields.${field}`),
                  });
                  const current = value[eye][field];
                  const invalid = Boolean(messageId(eye, field));

                  if (readOnly) {
                    return (
                      <td key={field} className="text-ink font-mono tabular-nums">
                        {field === 'prismBase' && current
                          ? t(`medical.refraction.bases.${current}`)
                          : current || '—'}
                      </td>
                    );
                  }

                  if (field === 'prismBase') {
                    return (
                      <td key={field}>
                        <select
                          aria-label={cellLabel}
                          aria-invalid={invalid || undefined}
                          aria-describedby={messageId(eye, field)}
                          value={current}
                          onChange={(event) => update(eye, field, event.target.value)}
                          className={cellClass(invalid)}
                        >
                          <option value="">{t('medical.refraction.noBase')}</option>
                          {Object.values(PrismBase).map((base) => (
                            <option key={base} value={base}>
                              {t(`medical.refraction.bases.${base}`)}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  }

                  const kind = POWER_KIND[field];
                  return (
                    <td key={field}>
                      <div className="flex">
                        {field === 'sphere' || field === 'cylinder' ? (
                          <button
                            type="button"
                            tabIndex={-1}
                            aria-label={t('medical.refraction.flipSign', { field: cellLabel })}
                            onClick={() => update(eye, field, flipSign(current))}
                            className="border-line text-ink hover:bg-canvas rounded-s-lg border border-e-0 px-2 font-semibold"
                          >
                            ±
                          </button>
                        ) : null}
                        <input
                          aria-label={cellLabel}
                          aria-invalid={invalid || undefined}
                          aria-describedby={messageId(eye, field)}
                          inputMode={field === 'axis' ? 'numeric' : 'decimal'}
                          autoComplete="off"
                          spellCheck={false}
                          value={current}
                          onChange={(event) => update(eye, field, event.target.value)}
                          onBlur={() => {
                            if (kind) {
                              const tidy = normalisePower(current, kind);
                              if (tidy !== current) update(eye, field, tidy);
                            }
                          }}
                          className={cn(
                            cellClass(invalid),
                            'font-mono tabular-nums',
                            (field === 'sphere' || field === 'cylinder') && 'rounded-s-none',
                          )}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {messages.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {messages.map((message) => (
            <li key={message.id} id={message.id} className="text-sm text-red-600">
              <span dir="ltr" className="font-mono">
                {message.abbreviation} {label(message.field)}
              </span>{' '}
              {t(message.error.key, message.error.values)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function cellClass(invalid: boolean): string {
  return cn(
    'border-line w-full min-w-0 rounded-lg border bg-white px-2 py-2 text-sm',
    'focus:border-brand-500 focus:ring-brand-100 focus:ring-2 focus:outline-none',
    invalid && 'border-red-400 focus:border-red-500 focus:ring-red-100',
  );
}

/** Turns the sign over without reading the number: an empty field becomes "-" so the digits can follow. */
function flipSign(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('-')) return `+${trimmed.slice(1)}`;
  if (trimmed.startsWith('+')) return `-${trimmed.slice(1)}`;
  return `-${trimmed}`;
}
